// AddProductPage.jsx
import { useEffect, useState, useRef, useCallback } from 'react';
import words from '../../../assets/words';
import { useSelector } from "react-redux"
import ProductsApiService from '../../../services/ProductsApiService';
import Select from '../../../components/ui/SelectInput';
import { styles } from './AddProductPage.styles';
import AddVariantModal from '../productProfile/modals/AddVariantModal';
import { HttpStatusCode } from 'axios';
import { useNavigate } from 'react-router-dom/dist/umd/react-router-dom.development';
import Button from '../../../components/ui/Button';
import { FaArrowLeft, FaArrowRight } from 'react-icons/fa';
import heic2any from 'heic2any';

const AddProductPage = () => {
    const { language } = useSelector(state => state.auth);
    const [newProductId, setNewProductId] = useState(null);
    const [currentStep, setCurrentStep] = useState(1); // 1: Create Product, 2: Add Variants
    const [formData, setFormData] = useState({
        nameEn: '',
        nameAr: '',
        descriptionEn: '',
        descriptionAr: '',
        brandId: '',
        collectionId: '',
        categoryId: '',
        subCategoryId: '',
        selectedNames: {}
    });

    const navigate = useNavigate();

    const [images, setImages] = useState([]);
    const [primaryImageIndex, setPrimaryImageIndex] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(false);
    const [dragActive, setDragActive] = useState(false);
    const [compressing, setCompressing] = useState(false);
    const [filterOptions, setFilterOptions] = useState({
        collections: [],
        categories: [],
        subCategories: [],
        brands: [],
    });

    const fileInputRef = useRef(null);
    const abortControllersRef = useRef([]);
    const containerRef = useRef(null);

    const t = useCallback((key) => words[key][language], [language]);

    // Reset form function
    const resetForm = useCallback(() => {
        setFormData({
            nameEn: '',
            nameAr: '',
            descriptionEn: '',
            descriptionAr: '',
            brandId: '',
            collectionId: '',
            categoryId: '',
            subCategoryId: '',
            selectedNames: {}
        });
        setImages([]);
        setPrimaryImageIndex(null);
        setError(null);
        setSuccess(false);
        setCurrentStep(1);
        setNewProductId(null);
    }, []);

    const handleFormChange = useCallback((e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    }, []);

    const handleDrag = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.type === "dragenter" || e.type === "dragover") {
            setDragActive(true);
        } else if (e.type === "dragleave") {
            setDragActive(false);
        }
    }, []);

    const handleDrop = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);
        processFiles(e.dataTransfer.files);
    }, []);

    const handleFileInput = useCallback((e) => {
        processFiles(e.target.files);
    }, []);

    // Image compression function
    const compressImage = useCallback((file) => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();

            const cleanup = () => {
                reader.onload = null;
                reader.onerror = null;
            };

            reader.onerror = () => {
                cleanup();
                reject(new Error('فشل قراءة الملف'));
            };

            reader.onload = (event) => {
                const img = new Image();

                img.onerror = () => {
                    cleanup();
                    reject(new Error('فشل تحميل الصورة'));
                };

                img.onload = () => {
                    try {
                        const canvas = document.createElement('canvas');
                        const ctx = canvas.getContext('2d');

                        const MAX_WIDTH = 1920;
                        const MAX_HEIGHT = 1920;

                        let width = img.width;
                        let height = img.height;

                        if (width > height) {
                            if (width > MAX_WIDTH) {
                                height = height * (MAX_WIDTH / width);
                                width = MAX_WIDTH;
                            }
                        } else {
                            if (height > MAX_HEIGHT) {
                                width = width * (MAX_HEIGHT / height);
                                height = MAX_HEIGHT;
                            }
                        }

                        canvas.width = width;
                        canvas.height = height;
                        ctx.drawImage(img, 0, 0, width, height);

                        canvas.toBlob(
                            (blob) => {
                                cleanup();
                                if (blob) {
                                    const compressedFile = new File(
                                        [blob],
                                        file.name.replace(/\.[^/.]+$/, ".webp"),
                                        { type: 'image/webp' }
                                    );
                                    resolve(compressedFile);
                                } else {
                                    reject(new Error('فشل ضغط الصورة'));
                                }
                            },
                            'image/webp',
                            0.85
                        );
                    } catch (err) {
                        cleanup();
                        reject(err);
                    }
                };

                img.src = event.target.result;
            };

            reader.readAsDataURL(file);
        });
    }, []);

    const processFiles = useCallback(async (files) => {
        if (!files || files.length === 0) return;

        setCompressing(true);
        setError(null);

        try {
            Array.from(files).forEach(file => console.log('File:', file.name, file.type, file.size));
            const imageFiles = Array.from(files).filter(file => file.type.startsWith('image/') || /\.(heic|heif)$/i.test(file.name));

            if (imageFiles.length === 0) {
                setError('الرجاء اختيار صور فقط');
                setCompressing(false);
                return;
            }

            // const compressedImages = await Promise.all(
            //     imageFiles.map(async (file) => {
            //         try {
            //             const compressedFile = await compressImage(normalizedFile);
            //             const preview = await new Promise((resolve, reject) => {
            //                 const reader = new FileReader();
            //                 reader.onload = (e) => resolve(e.target.result);
            //                 reader.onerror = reject;
            //                 reader.readAsDataURL(compressedFile);
            //             });

            //             return {
            //                 id: Date.now() + Math.random(),
            //                 file: compressedFile,
            //                 preview,
            //                 name: compressedFile.name
            //             };
            //         } catch (err) {
            //             console.error('Error processing image:', err);
            //             return null;
            //         }
            //     })
            // );

            const compressedImages = await Promise.all(
                imageFiles.map(async (file) => {
                    try {
                        const finalFile = await processSingleFile(file);

                        if (!finalFile) return null;

                        const preview = await new Promise((resolve, reject) => {
                            const reader = new FileReader();
                            reader.onload = (e) => resolve(e.target.result);
                            reader.onerror = reject;
                            reader.readAsDataURL(finalFile);
                        });

                        return {
                            id: Date.now() + Math.random(),
                            file: finalFile,
                            preview,
                            name: finalFile.name,
                            isFallback: finalFile === file // 🔥 useful
                        };

                    } catch (err) {
                        console.error(err);
                        return null;
                    }
                })
            );

            const validImages = compressedImages.filter(img => img !== null);

            if (validImages.length > 0) {
                setImages(prev => [...prev, ...validImages]);
                if (primaryImageIndex === null) {
                    setPrimaryImageIndex(0);
                }
            } else {
                setError('فشل معالجة الصور');
            }
        } catch (error) {
            console.error('Error processing files:', error);
            setError('حدث خطأ أثناء معالجة الصور');
        } finally {
            setCompressing(false);
        }
    }, [compressImage, primaryImageIndex]);

    const removeImage = useCallback((index) => {
        setImages(prev => prev.filter((_, i) => i !== index));

        if (primaryImageIndex === index) {
            setPrimaryImageIndex(images.length > 1 ? 0 : null);
        } else if (primaryImageIndex > index) {
            setPrimaryImageIndex(prev => prev - 1);
        }
    }, [primaryImageIndex, images.length]);

    const setPrimaryImage = useCallback((index) => {
        setPrimaryImageIndex(index);
    }, []);

    const processSingleFile = async (file) => {
        let workingFile = file;

        try {
            // 1. Normalize
            if (isHeicOrHeif(file)) {
                workingFile = await tryConvertHeic(file);
            }

            // 2. Try compress
            try {
                const compressed = await compressImage(workingFile);
                return compressed;
            } catch (compressError) {
                console.warn("⚠️ Compression failed, using original");
                return workingFile; // 🔥 fallback
            }

        } catch (err) {
            console.error("❌ File processing failed:", err);
            return null;
        }
    };

    const isHeicOrHeif = (file) => {
        const type = file.type?.toLowerCase();
        const name = file.name?.toLowerCase();

        return (
            type === "image/heic" ||
            type === "image/heif" ||
            name.endsWith(".heic") ||
            name.endsWith(".heif")
        );
    };

    const tryConvertHeic = async (file) => {
        try {
            const convertedBlob = await heic2any({
                blob: file,
                toType: "image/jpeg",
                quality: 0.9
            });

            return new File(
                [convertedBlob],
                file.name.replace(/\.(heic|heif)$/i, ".jpg"),
                { type: "image/jpeg" }
            );
        } catch (err) {
            console.warn("⚠️ HEIC conversion failed, using original file");
            return file;
        }
    };

    const validateForm = useCallback(() => {
        const validations = [
            { condition: !formData.nameEn.trim(), message: 'Product name in English is required' },
            { condition: !formData.nameAr.trim(), message: 'Product name in Arabic is required' },
            { condition: !formData.brandId, message: 'Brand is required' },
            { condition: !formData.collectionId, message: 'Collection is required' },
            { condition: !formData.categoryId, message: 'Category is required' },
            { condition: !formData.subCategoryId, message: 'Sub Category is required' },
            { condition: images.length === 0, message: 'At least one image is required' },
            { condition: primaryImageIndex === null, message: 'Please select a primary image' },
            { condition: primaryImageIndex >= images.length, message: 'Invalid primary image index' }
        ];

        for (const validation of validations) {
            if (validation.condition) {
                setError(validation.message);
                return false;
            }
        }
        return true;
    }, [formData, images, primaryImageIndex]);

    // Step 1: Create Product Only
    // const handleCreateProduct = useCallback(async (e) => {
    //     e.preventDefault();
    //     setError(null);
    //     setSuccess(false);

    //     if (!validateForm()) return;

    //     setLoading(true);

    //     try {
    //         const multipartFormData = new FormData();
    //         images.forEach(({ file }) => {
    //             multipartFormData.append('images', file);
    //         });

    //         multipartFormData.append('primaryImageIndex', primaryImageIndex.toString());
    //         multipartFormData.append('nameEn', formData.nameEn.trim());
    //         multipartFormData.append('nameAr', formData.nameAr.trim());
    //         multipartFormData.append('descriptionEn', formData.descriptionEn.trim());
    //         multipartFormData.append('descriptionAr', formData.descriptionAr.trim());
    //         multipartFormData.append('brandId', formData.brandId.toString());
    //         multipartFormData.append('collectionId', formData.collectionId.toString());
    //         multipartFormData.append('categoryId', formData.categoryId.toString());
    //         multipartFormData.append('subCategoryId', formData.subCategoryId.toString());

    //         const response = await ProductsApiService.createNewProduct(multipartFormData);

    //         if (response?.status !== HttpStatusCode.Ok) {
    //             setError(response?.message || 'حدث خطأ أثناء إضافة المنتج');
    //             setLoading(false);
    //             return;
    //         }

    //         setNewProductId(response?.data);
    //         setSuccess(true);
    //         setCurrentStep(2); // Move to Add Variants step

    //     } catch (error) {
    //         console.error('Error creating product:', error);
    //         setError(error?.message || 'حدث خطأ أثناء إضافة المنتج');
    //     } finally {
    //         setLoading(false);
    //     }
    // }, [formData, images, primaryImageIndex, validateForm]);

    const handleCreateProduct = useCallback(async (e) => {
        e.preventDefault();
        setError(null);
        setSuccess(false);

        if (!validateForm()) return;

        setLoading(true);

        try {
            const multipartFormData = new FormData();

            images.forEach(({ file }) => {
                multipartFormData.append('images', file);
            });

            const productData = {
                primaryImageIndex: primaryImageIndex,
                nameEn: formData.nameEn.trim(),
                nameAr: formData.nameAr.trim(),
                descriptionEn: formData.descriptionEn.trim(),
                descriptionAr: formData.descriptionAr.trim(),
                brandId: formData.brandId,
                collectionId: formData.collectionId,
                categoryId: formData.categoryId,
                subCategoryId: formData.subCategoryId
            };
            multipartFormData.append('productData', new Blob([JSON.stringify(productData)], {
                type: 'application/json'
            }));

            // multipartFormData.append('primaryImageIndex',Number(primaryImageIndex));
            // multipartFormData.append('nameEn', formData.nameEn.toString().trim());
            // multipartFormData.append('nameAr', formData.nameAr.toString().trim());
            // multipartFormData.append('descriptionEn', formData.descriptionEn.toString().trim());
            // multipartFormData.append('descriptionAr', formData.descriptionAr.toString().trim());
            // multipartFormData.append('brandId', Number(formData.brandId));
            // multipartFormData.append('collectionId', Number(formData.collectionId));
            // multipartFormData.append('categoryId', Number(formData.categoryId));
            // multipartFormData.append('subCategoryId', formData.subCategoryId.toString());

            const response = await ProductsApiService.createNewProduct(multipartFormData);

            if (response?.status >= HttpStatusCode.BadRequest) {
                setError(response?.message || 'حدث خطأ أثناء إضافة المنتج');
                setLoading(false);
                return;
            }

            setNewProductId(response?.data);
            setSuccess(true);
            setCurrentStep(2);

        } catch (error) {
            console.error('Error creating product:', error);
            setError(error?.response?.data?.message || error?.message || 'حدث خطأ أثناء إضافة المنتج');
        } finally {
            setLoading(false);
        }
    }, [formData, images, primaryImageIndex, validateForm]);


    const handleFilterChange = useCallback((field, option, optionName) => {
        if (!option) return;

        setFormData(prev => {
            const newData = {
                ...prev,
                [field]: option.value,
                selectedNames: {
                    ...prev.selectedNames,
                    [optionName]: option.label
                }
            };

            if (field === 'collectionId') {
                newData.categoryId = '';
                newData.subCategoryId = '';
                newData.selectedNames.category = '';
                newData.selectedNames.subCategory = '';
            } else if (field === 'categoryId') {
                newData.subCategoryId = '';
                newData.selectedNames.subCategory = '';
            }

            return newData;
        });
    }, []);

    const fetchFilterData = useCallback(async () => {
        try {
            setLoading(true);
            const data = await ProductsApiService.getProductFilters(
                formData.collectionId,
                formData.categoryId
            );
            setFilterOptions(data);
        } catch (error) {
            console.error('Error fetching filter data:', error);
            setError('فشل تحميل البيانات');
        } finally {
            setLoading(false);
        }
    }, [formData.collectionId, formData.categoryId]);

    useEffect(() => {
        fetchFilterData();
    }, [fetchFilterData]);

    useEffect(() => {
        if (error || success) {
            if (containerRef.current) {
                containerRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    }, [error, success]);

    useEffect(() => {
        return () => {
            abortControllersRef.current.forEach(controller => controller.abort());
        };
    }, []);

    // Step 2 Content - Add Variants
    const renderStep2 = () => (
        <div style={styles.stepContainer}>
            <div style={styles.successMessage}>
                ✅ {t('PRODUCT_CREATED_SUCCESS') || 'Product created successfully! Now add variants.'}
            </div>

            <div style={styles.stepContent}>
                <h2 style={styles.sectionTitle}>🎉 {t('ADD_VARIANTS') || 'Add Variants'}</h2>
                <p style={styles.stepDescription}>
                    {t('PRODUCT_ID') || 'Product ID'}: <strong>{newProductId}</strong>
                </p>

                <AddVariantModal
                    show={true}
                    product={{
                        id: newProductId,
                        brand: {
                            nameEn: filterOptions.brands.find(b => b.id === parseInt(formData.brandId))?.nameEn || '',
                            nameAr: filterOptions.brands.find(b => b.id === parseInt(formData.brandId))?.nameAr || ''
                        }
                    }}
                    onHide={() => {
                        resetForm();
                    }}
                    onSuccess={() => {
                        setSuccess(true);
                    }}
                />
            </div>
        </div>
    );

    // Step 1 Content - Create Product Form
    const renderStep1 = () => (
        <form onSubmit={handleCreateProduct}>
            <div style={styles.formSection}>
                <h2 style={styles.sectionTitle}>📋 {t('BASIC_INFO')}</h2>

                <div style={styles.formRow}>
                    <div style={styles.formGroup}>
                        <label style={styles.label}>
                            {t('NAME_EN')} <span style={styles.required}>{t('REQUIRED')}</span>
                        </label>
                        <input
                            type="text"
                            name="nameEn"
                            value={formData.nameEn}
                            onChange={handleFormChange}
                            placeholder="Enter product name in English"
                            style={styles.input}
                        />
                    </div>
                    <div style={styles.formGroup}>
                        <label style={styles.label}>
                            {t('NAME_AR')} <span style={styles.required}>{t('REQUIRED')}</span>
                        </label>
                        <input
                            type="text"
                            name="nameAr"
                            value={formData.nameAr}
                            onChange={handleFormChange}
                            placeholder="أدخل اسم المنتج بالعربية"
                            style={{ ...styles.input, direction: 'rtl' }}
                        />
                    </div>
                </div>

                <div style={styles.formRow}>
                    <div style={styles.formGroup}>
                        <label style={styles.label}>{t('DESC_EN')}</label>
                        <textarea
                            name="descriptionEn"
                            value={formData.descriptionEn}
                            onChange={handleFormChange}
                            placeholder="Enter product description in English"
                            style={styles.textarea}
                        />
                    </div>
                    <div style={styles.formGroup}>
                        <label style={styles.label}>{t('DESC_AR')}</label>
                        <textarea
                            name="descriptionAr"
                            value={formData.descriptionAr}
                            onChange={handleFormChange}
                            placeholder="أدخل وصف المنتج بالعربية"
                            style={{ ...styles.textarea, direction: 'rtl' }}
                        />
                    </div>
                </div>

                <div style={styles.filterSection}>
                    <div style={styles.filterGroup}>
                        <label style={styles.label}>{words.COLLECTION[language]}</label>
                        <Select
                            name="collectionId"
                            value={{
                                value: formData.collectionId,
                                label: formData.selectedNames?.collection || words.ALL_COLLECTIONS[language]
                            }}
                            onChange={(e) => handleFilterChange('collectionId', e, "collection")}
                            options={[
                                { value: 0, label: words.ALL_COLLECTIONS[language] },
                                ...filterOptions.collections.map(collection => ({
                                    value: collection.id,
                                    label: language === 'en' ? collection.nameEn : collection.nameAr
                                }))
                            ]}
                        />
                    </div>

                    <div style={styles.filterGroup}>
                        <label style={styles.label}>{words.CATEGORY[language]}</label>
                        <Select
                            name="categoryId"
                            value={{
                                value: formData.categoryId || 0,
                                label: formData.selectedNames?.category || words.ALL_CATEGORIES[language]
                            }}
                            onChange={(e) => handleFilterChange('categoryId', e, "category")}
                            options={[
                                { value: 0, label: words.ALL_CATEGORIES[language] },
                                ...filterOptions.categories.map(category => ({
                                    value: category.id,
                                    label: language === 'en' ? category.nameEn : category.nameAr
                                }))
                            ]}
                        />
                    </div>

                    <div style={styles.filterGroup}>
                        <label style={styles.label}>{words.SUB_CATEGORY[language]}</label>
                        <Select
                            name="subCategoryId"
                            value={{
                                value: formData.subCategoryId,
                                label: formData.selectedNames?.subCategory || words.ALL_SUB_CATEGORIES[language]
                            }}
                            onChange={(e) => handleFilterChange('subCategoryId', e, "subCategory")}
                            options={[
                                { value: 0, label: words.ALL_SUB_CATEGORIES[language] },
                                ...filterOptions.subCategories.map(subCategory => ({
                                    value: subCategory.id,
                                    label: language === 'en' ? subCategory.nameEn : subCategory.nameAr
                                }))
                            ]}
                        />
                    </div>

                    <div style={styles.filterGroup}>
                        <label style={styles.label}>{words.BRAND[language]}</label>
                        <Select
                            name="brandId"
                            value={{
                                value: formData.brandId,
                                label: formData.selectedNames?.brand || words.ALL_BRANDS[language]
                            }}
                            onChange={(e) => handleFilterChange('brandId', e, "brand")}
                            options={[
                                { value: 0, label: words.ALL_BRANDS[language] },
                                ...filterOptions.brands.map(brand => ({
                                    value: brand.id,
                                    label: language === 'en' ? brand.nameEn : brand.nameAr
                                }))
                            ]}
                        />
                    </div>
                </div>
            </div>

            <div style={styles.formSection}>
                <h2 style={styles.sectionTitle}>🖼️ {t('IMAGES')}</h2>

                <div
                    onDragEnter={handleDrag}
                    onDragLeave={handleDrag}
                    onDragOver={handleDrag}
                    onDrop={handleDrop}
                    style={{
                        ...styles.dragDropArea,
                        backgroundColor: dragActive ? '#e0e7ff' : '#f7fafc',
                        borderColor: dragActive ? '#667eea' : '#cbd5e0'
                    }}
                >
                    <div style={styles.dragDropContent}>
                        <div style={{ fontSize: '2.5rem', marginBottom: '0.5rem' }}>📤</div>
                        <h3 style={styles.dragDropTitle}>{t('UPLOAD_IMAGES')}</h3>
                        <p style={styles.dragDropText}>{t('DRAG_DROP')}</p>
                        <input
                            ref={fileInputRef}
                            type="file"
                            multiple
                            // accept="image/*"
                            onChange={handleFileInput}
                            style={styles.fileInput}
                            id="fileInput"
                        />
                        <button
                            type="button"
                            onClick={() => fileInputRef.current?.click()}
                            style={styles.selectBtn}
                            disabled={compressing}
                        >
                            {compressing ? '⏳ جاري الضغط...' : t('CLICK_TO_SELECT')}
                        </button>
                    </div>
                </div>

                {images.length > 0 && (
                    <div style={styles.imageCounter}>
                        ✅ {images.length} {t('IMAGE_COUNT')}
                    </div>
                )}

                {images.length > 0 && (
                    <div style={styles.imagesGrid}>
                        {images.map((image, index) => (
                            <div
                                key={image.id}
                                style={{
                                    ...styles.imageCard,
                                    borderColor: primaryImageIndex === index ? '#667eea' : '#e2e8f0',
                                    backgroundColor: primaryImageIndex === index ? '#eef2ff' : 'white'
                                }}
                            >
                                <div style={styles.imagePreview}>
                                    <img src={image.preview} alt={`Preview ${index + 1}`} style={styles.previewImg} />
                                    {primaryImageIndex === index && (
                                        <div style={styles.primaryBadge}>⭐</div>
                                    )}
                                </div>
                                <div style={styles.imageActions}>
                                    {primaryImageIndex !== index && (
                                        <button
                                            onClick={() => setPrimaryImage(index)}
                                            style={styles.btnPrimary}
                                        >
                                            {t('SET_PRIMARY')}
                                        </button>
                                    )}
                                    <button
                                        onClick={() => removeImage(index)}
                                        style={styles.btnRemove}
                                    >
                                        {t('REMOVE')}
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            <div style={styles.formActions}>
                <button
                    type="button"
                    onClick={resetForm}
                    style={styles.btnCancel}
                    disabled={loading}
                >
                    {t('CANCEL')}
                </button>
                <button
                    type="submit"
                    style={styles.btnSubmit}
                    disabled={loading || compressing}
                >
                    {loading ? '⏳ جاري إنشاء المنتج...' : t('CREATE_PRODUCT') || 'Create Product'}
                </button>
            </div>
        </form>
    );

    return (
        <div style={styles.container} ref={containerRef}>
            <div className='my-4'>
                <Button onClick={() => navigate('/products')}>
                    {language === "en"&& (<FaArrowLeft className='mx-1'/>)}
                    {words.BACK_TO_PRODUCTS[language]}
                    {language !== "en"&& (<FaArrowLeft className='mx-1'/>)}
                </Button>
            </div>
            <div style={styles.pageHeader}>
                <h1 style={styles.title}>{t('ADD_PRODUCT')}</h1>
                <div style={styles.stepIndicator}>
                    <span style={styles.stepText}>
                        {t('STEP') || 'Step'} {currentStep} {t('OF') || 'of'} 2
                    </span>
                </div>
            </div>


            {error && (
                <div style={styles.errorMessage}>
                    ⚠️ {error}
                </div>
            )}

            {compressing && (
                <div style={styles.compressingMessage}>
                    ⏳ جاري ضغط الصور...
                </div>
            )}

            {currentStep === 1 ? renderStep1() : renderStep2()}
        </div>
    );
};

export default AddProductPage;